
// src/pages/public/ServicesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../config/firebase";
import { collection, doc, onSnapshot, orderBy, query } from "firebase/firestore";

/** --- Simple hook for phone detection --- **/
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const handler = () => {
      if (typeof window === "undefined") return;
      setIsMobile(window.innerWidth <= breakpoint);
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);

  return isMobile;
}

export default function ServicesPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [categories, setCategories] = useState([]); // [{id,name}]
  const [activeCatId, setActiveCatId] = useState(null);
  const [activeCatName, setActiveCatName] = useState("");
  const [services, setServices] = useState([]); // services of active category

  // Global selection across ALL categories:
  // key = `${catId}__${svcId}` -> { id, catId, catName, name, price(Number), durationMin(Number) }
  const [selectedMap, setSelectedMap] = useState({});

  /* ---------- live categories ---------- */
  useEffect(() => {
    const qCats = query(collection(db, "categories"), orderBy("name", "asc"));
    const unsub = onSnapshot(
      qCats,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setCategories(rows);
        if (!activeCatId && rows.length) {
          setActiveCatId(rows[0].id);
          setActiveCatName(rows[0].name || "Category");
        }
      },
      (err) => console.error("categories onSnapshot error:", err)
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- live services of active category ---------- */
  useEffect(() => {
    if (!activeCatId) return;
    const catRef = doc(db, "categories", activeCatId);
    const qSvcs = query(collection(catRef, "services"), orderBy("name", "asc"));
    const unsub = onSnapshot(
      qSvcs,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setServices(rows);
      },
      (err) => console.error("services onSnapshot error:", err)
    );
    return () => unsub();
  }, [activeCatId]);

  const onPickCategory = (c) => {
    setActiveCatId(c.id);
    setActiveCatName(c.name || "Category");
  };

  /* ---------- selection helpers ---------- */
  const isChecked = (catId, svcId) => !!selectedMap[`${catId}__${svcId}`];

  const toggleService = (catId, catName, svc) => {
    const key = `${catId}__${svc.id}`;
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = {
          key,
          id: svc.id,
          catId,
          catName,
          name: svc.name,
          price: numOrZero(svc.price),
          durationMin: numOrZero(svc.durationMin ?? svc.duration),
          from: (svc.type || "").toLowerCase() === "from",
        };
      }
      return next;
    });
  };

  /* ---------- summary across ALL categories ---------- */
  const selectedList = useMemo(() => Object.values(selectedMap), [selectedMap]);

  const totals = useMemo(() => {
    const count = selectedList.length;
    const totalPrice = selectedList.reduce((sum, s) => sum + (s.price || 0), 0);
    const totalMin = selectedList.reduce((sum, s) => sum + (s.durationMin || 0), 0);
    return { count, totalPrice, totalMin };
  }, [selectedList]);

  /* ---------- navigation ---------- */
  const goNext = () => {
    if (totals.count === 0) return;
    navigate("/select-staff", {
      state: {
        services: selectedList,
        totals: {
          count: totals.count,
          totalPrice: totals.totalPrice,
          totalMin: totals.totalMin,
          totalPriceLabel: formatCAD(totals.totalPrice),
          totalDurLabel: formatDuration(totals.totalMin),
        },
      },
    });
  };

  // responsive containers
  const heroBoxStyle = isMobile ? styles.heroBoxMobile : styles.heroBoxDesktop;
  const containerStyle = isMobile ? styles.containerMobile : styles.containerDesktop;
  const gridStyle = isMobile ? styles.gridMobile : styles.gridDesktop;
  const nextRowStyle = isMobile ? styles.nextRowMobile : styles.nextRowDesktop;

  return (
    <div style={styles.screen}>
      {/* Header is handled in AppRouter */}

      {/* Hero */}
      <div style={heroBoxStyle}>
        <div
          style={{
            ...styles.heroImage,
            backgroundImage:
              "url(https://assets.api.uizard.io/api/cdn/stream/597fae2c-5bf0-46f2-ac43-e83205eb5d64.png)",
          }}
        />
        <div
          style={{
            ...styles.heroOverlay,
            ...(isMobile ? styles.heroOverlayMobile : {}),
          }}
        >
          <div style={{ display: "grid", gap: 6 }}>
            <div style={styles.heroTitle}>613Nail Hub</div>
            <div style={styles.heroMeta}>
              📍 598 Rideau St, Ottawa, ON K1N 6C6 <br></br> ☎︎ (613) 327-4049
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div style={containerStyle}>
        <div style={styles.sectionTitle}>Services</div>

        <div style={gridStyle}>
          {/* Left: Categories */}
          <div style={styles.catPanel}>
            {categories.length === 0 && (
              <div style={styles.emptyText}>No categories yet</div>
            )}
            {categories.map((c) => {
              const active = c.id === activeCatId;
              return (
                <button
                  key={c.id}
                  onClick={() => onPickCategory(c)}
                  style={{
                    ...styles.catItem,
                    ...(active ? styles.catItemActive : {}),
                  }}
                >
                  {c.name || "Category"}
                </button>
              );
            })}
          </div>

          {/* Right: Services of selected category */}
          <div style={styles.servicesPanel}>
            <div style={styles.catHeading}>
              {activeCatName || (categories.length ? "Select a category" : "…")}
            </div>

            {services.length === 0 && (
              <div style={styles.emptyText}>No services in this category</div>
            )}

            {services.length > 0 && (
              <div style={{ display: "grid", gap: 12 }}>
                {services.map((s) => {
                  const checked = isChecked(activeCatId, s.id);
                  return (
                    <ServiceRow
                      key={s.id}
                      name={s.name}
                      durationLabel={durationLabel(s)}
                      priceLabel={priceLabel(s)}
                      from={(s.type || "").toLowerCase() === "from"}
                      checked={checked}
                      onToggle={() =>
                        toggleService(activeCatId, activeCatName, s)
                      }
                    />
                  );
                })}
              </div>
            )}

            {/* Summary + Next */}
            <div style={nextRowStyle}>
              <div
                style={{
                  marginRight: isMobile ? 0 : "auto",
                  color: "#6b7280",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {totals.count > 0
                  ? `${totals.count} selected • ${formatCAD(
                      totals.totalPrice
                    )} • ${formatDuration(totals.totalMin)}`
                  : "Select one or more services"}
              </div>
              <button
                style={{
                  ...styles.nextBtn,
                  ...(isMobile ? { width: "100%" } : {}),
                  opacity: totals.count === 0 ? 0.7 : 1,
                  cursor: totals.count === 0 ? "not-allowed" : "pointer",
                }}
                disabled={totals.count === 0}
                onClick={goNext}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --- Row (no navigation here, just toggle) --- */
function ServiceRow({ name, durationLabel, priceLabel, from, checked, onToggle }) {
  return (
    <div style={styles.card}>
      <label style={styles.cardLeft}>
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          style={styles.checkbox}
        />
        <div>
          <div style={styles.cardTitle}>{name}</div>
          <div style={styles.cardMeta}>{durationLabel}</div>
        </div>
      </label>

      <div style={styles.priceWrap}>
        <div style={styles.pricePill}>{priceLabel}</div>
        {from && <div style={styles.fromText}>From</div>}
      </div>
    </div>
  );
}

/* --- Helpers --- */
function numOrZero(v) {
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : 0;
}
function durationLabel(svc) {
  const min = numOrZero(svc.durationMin ?? svc.duration);
  return min ? `${min} min` : "—";
}
function priceLabel(svc) {
  const n = numOrZero(svc.price);
  return n ? formatCAD(n) : "—";
}
function formatCAD(n) {
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `C$ ${n}`;
  }
}
function formatDuration(totalMin) {
  if (!totalMin) return "0 min";
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

/* --- Styles (desktop + mobile variants) --- */
const styles = {
  screen: {
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    fontFamily:
      "Work Sans, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  },

  // hero
  heroBoxDesktop: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 20px",
    position: "relative",
  },
  heroBoxMobile: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "0 16px",
    position: "relative",
  },
  heroImage: {
    width: "100%",
    height: 210,
    borderRadius: 4,
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  heroOverlay: {
    position: "absolute",
    left: 40,
    top: 64,
    display: "flex",
    gap: 12,
    alignItems: "center",
    background: "rgba(113,93,89,0.85)",
    padding: "10px 14px",
    borderRadius: 10,
    color: "#fff",
  },
  heroOverlayMobile: {
    left: 16,
    top: 40,
    right: 16,
  },
  badgeNum: {
    fontSize: 36,
    fontWeight: 300,
    lineHeight: "1",
    padding: "6px 10px",
  },
  heroTitle: { fontSize: 18, fontWeight: 700 },
  heroMeta: { fontSize: 13, fontWeight: 500, opacity: 0.95 },

  // container
  containerDesktop: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "12px 20px 40px",
  },
  containerMobile: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "12px 16px 32px",
  },

  sectionTitle: {
    fontSize: 32,
    fontWeight: 700,
    color: "#101827",
    margin: "14px 0 14px",
  },

  // grid
  gridDesktop: {
    display: "grid",
    gridTemplateColumns: "280px 1fr",
    gap: 24,
    alignItems: "start",
  },
  gridMobile: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 16,
    alignItems: "start",
  },

  /* Left panel */
  catPanel: {
    background: "#ffffff",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    padding: 14,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  catItem: {
    textAlign: "left",
    background: "transparent",
    border: 0,
    padding: "10px 12px",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 700,
    color: "#030303",
    cursor: "pointer",
  },
  catItemActive: {
    background: "#eee9f2",
    boxShadow: "inset 0 0 0 1px #d7d1dc",
  },

  /* Right panel */
  servicesPanel: {
    display: "grid",
    gap: 12,
  },
  catHeading: {
    fontSize: 16,
    fontWeight: 700,
    color: "#030303",
  },

  card: {
    background: "#ffffff",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    padding: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  cardLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    cursor: "pointer",
  },
  checkbox: {
    width: 18,
    height: 18,
    accentColor: "#5c5470",
  },
  cardTitle: {
    color: "#5c5470",
    fontSize: 16,
    fontWeight: 600,
    lineHeight: "22px",
  },
  cardMeta: {
    color: "#6b7280",
    fontSize: 14,
    fontWeight: 600,
    lineHeight: "22px",
    marginTop: 4,
  },
  priceWrap: {
    display: "grid",
    justifyItems: "end",
    alignContent: "center",
    gap: 4,
    minWidth: 90,
  },
  pricePill: {
    background: "#5c5470",
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
    padding: "6px 10px",
    borderRadius: 20,
    minWidth: 68,
    textAlign: "center",
  },
  fromText: {
    color: "#6b7280",
    fontSize: 12,
    fontWeight: 600,
  },

  // summary/footer row
  nextRowDesktop: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 12,
    paddingTop: 12,
  },
  nextRowMobile: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 10,
    paddingTop: 16,
  },
  nextBtn: {
    background: "#5c5470",
    color: "#fff",
    border: 0,
    borderRadius: 22,
    padding: "10px 18px",
    fontWeight: 700,
  },

  emptyText: {
    color: "#9aa0a6",
    fontSize: 14,
    fontStyle: "italic",
  },
};
